import {useEffect, useRef, useState} from 'react';

/**
 * How long warehouse-scoped inventory may hold the PDP CTA before we fall back
 * to the site-default orderable flags already on the product payload.
 */
export const PDP_INVENTORY_WAIT_MS = 1500;

/**
 * Cap how long a pending flag is allowed to block the UI.
 *
 * Returns the flag unchanged until it has been continuously true for `timeoutMs`,
 * after which it reads as false until the underlying flag clears. Use this where a
 * slow dependency should degrade to a best-effort answer instead of an endless
 * spinner. The timer restarts whenever the flag goes false and true again.
 *
 * @param {boolean} isPending
 * @param {number} [timeoutMs=PDP_INVENTORY_WAIT_MS]
 * @returns {boolean}
 */
export function useBoundedPending(isPending, timeoutMs = PDP_INVENTORY_WAIT_MS) {
  const [hasExpired, setHasExpired] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!isPending) {
      setHasExpired(false);

      return undefined;
    }

    timeoutRef.current = setTimeout(() => setHasExpired(true), timeoutMs);

    return () => clearTimeout(timeoutRef.current);
  }, [isPending, timeoutMs]);

  return isPending && !hasExpired;
}

export default useBoundedPending;
